/**
 * Chrome state: the sheet, the modal, the gear menu, toasts, the camera view,
 * full screen, and the accent colour the whole glass palette is derived from.
 */

import { createSignal } from 'solid-js';
import { hsl, hueDelta } from '../lib/color.ts';
import { clamp } from '../lib/math.ts';
import type { Palette } from '../lib/palette.ts';
import { HOME_VIEW, type ViewName } from '../three/views.ts';
import type { Playlist } from '../types/media.ts';

export type SheetView = 'home' | 'playlist' | 'search' | 'queue';
export type ModalKind = 'help' | 'settings' | 'keys';

export interface SheetState {
  open: boolean;
  view: SheetView;
  title: string;
  /** The playlist a `playlist` view is showing, for re-opening after a change. */
  playlist: Playlist | null;
}

export interface Toast {
  id: number;
  message: string;
  ms: number;
}

export const [booting, setBooting] = createSignal(true);
export const [bootProgress, setBootProgress] = createSignal(0);
export const [bootMessage, setBootMessage] = createSignal('Warming up the deck');
export const [ready, setReady] = createSignal(false);

export const [sheet, setSheet] = createSignal<SheetState>({
  open: false,
  view: 'home',
  title: 'Library',
  playlist: null,
});
/**
 * The search field, and what is actually being searched.
 *
 * Two signals, because they move at different speeds: the field has to echo
 * every keystroke, while the term behind the query settles first — one is
 * what you typed, the other is what it cost.
 */
export const [searchText, setSearchText] = createSignal('');
export const [searchTerm, setSearchTerm] = createSignal('');

export const [modal, setModal] = createSignal<ModalKind | null>(null);
/** The gear menu in the top-right corner. */
export const [menuOpen, setMenuOpen] = createSignal(false);
export const [view, setView] = createSignal<ViewName>(HOME_VIEW);
/** Whether the document is full screen right now, however it got there. */
export const [fullscreen, setFullscreen] = createSignal(false);
/** Whether the one-time full-screen offer is showing. */
export const [fullscreenOffer, setFullscreenOffer] = createSignal(false);
export const [youtubeCardOpen, setYoutubeCardOpen] = createSignal(false);
export const [accent, setAccent] = createSignal('#2FBF71');

const [toasts, setToasts] = createSignal<readonly Toast[]>([]);

export { toasts };

let toastId = 0;
const MAX_TOASTS = 3;

/** Shows a transient message. Older toasts fall off the top of the stack. */
export function toast(message: string, ms = 2600): void {
  if (!message) return;
  const entry: Toast = { id: ++toastId, message, ms };
  setToasts((list) => [...list, entry].slice(-MAX_TOASTS));
  setTimeout(() => {
    setToasts((list) => list.filter((t) => t.id !== entry.id));
  }, ms);
}

/**
 * How many times a panel has been opened.
 *
 * The body keys on it, so every opening is a fresh subtree — which is what
 * makes reopening a panel that failed a real retry, rather than a second look
 * at a latched error boundary. Closing does not bump it: the sheet slides out
 * over half a second and should still have its contents while it goes.
 */
const [sheetOpenings, bumpOpenings] = createSignal(0);

export { sheetOpenings };

export function openSheet(view: SheetView, title: string, playlist: Playlist | null = null): void {
  setSheet({ open: true, view, title, playlist });
  bumpOpenings((n) => n + 1);
}

export function closeSheet(): void {
  setSheet((s) => ({ ...s, open: false }));
}

/** True when the sheet is already showing this view — the buttons toggle. */
export const sheetShowing = (v: SheetView): boolean => {
  const s = sheet();
  return s.open && s.view === v;
};

/* The glass stays green by design: the artwork is only allowed to nudge its
   shade within a green band, so the UI never stops reading as one product. */
const GREEN = 148;

export function accentFor(palette: Palette | null): string {
  if (!palette) return hsl(GREEN, 0.6, 0.42);
  const h = clamp(GREEN + hueDelta(GREEN, palette.hue) * 0.16, 112, 172);
  const s = clamp(0.46 + (palette.sat || 0.4) * 0.28, 0.46, 0.68);
  return hsl(h, s, 0.42);
}

/** The ring lives on the deck, so it can lean further into the album's colour. */
export function ringAccentFor(palette: Palette | null): string {
  if (!palette) return hsl(GREEN, 0.66, 0.46);
  const h = clamp(GREEN + hueDelta(GREEN, palette.hue) * 0.4, 96, 200);
  const s = clamp(0.5 + (palette.sat || 0.4) * 0.34, 0.5, 0.8);
  return hsl(h, s, 0.46);
}

/** Retints the whole green family from the artwork so the glass stays coherent. */
export function applyAccent(hex: string): void {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return;
  const n = Number.parseInt(m[1] as string, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  const root = document.documentElement.style;
  const dim = (v: number) => Math.round(v * 0.72);
  root.setProperty('--accent', hex);
  root.setProperty('--accent-deep', `rgb(${dim(r)}, ${dim(g)}, ${dim(b)})`);
  root.setProperty('--accent-soft', `rgba(${r}, ${g}, ${b}, .16)`);
  root.setProperty('--accent-glow', `rgba(${r}, ${g}, ${b}, .40)`);
  root.setProperty('--glass-edge-2', `rgba(${r}, ${g}, ${b}, .16)`);
  setAccent(hex);
}
